import { useTranslation } from "react-i18next";
import { useRouter } from "@tanstack/react-router";
import { Button, Card, LoadingSpinner } from "@app-components";
import styles from "@/styles/BuildingContact.module.scss";

/**
 * The route's `beforeLoad` could not fetch the user's buildings (its retries
 * ran out), so the page cannot tell a member from a stranger.
 *
 * BuildingUserProvider shows its own retry screen only when the failure left it
 * without a building to render — and a QR URL that already carries a valid
 * `?building=` leaves it with one, because the building query is keyed on that
 * value and answers from its own endpoint. That is every reload of a scanned
 * page, and the return from login, which `beforeLoad` sends back here with
 * `?building=` set. Without this the route fell through to the app-wide error
 * boundary, which only offers a full page reload.
 *
 * `router.invalidate()` re-runs `beforeLoad`, and the membership entry holds no
 * data after a failure, so it asks again rather than replaying the error. No
 * `reset()`: the match object is the boundary's reset key, so it clears itself
 * once the route re-runs — calling it here would only drop this screen before
 * the retry it started has a chance to land.
 */
export default function ContactLoadError() {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <div className={styles.column}>
      <Card
        as="section"
        className={styles.notice}
        aria-labelledby="contact-error-heading"
      >
        <h1 id="contact-error-heading">{t("LOAD_ERROR.HEADER")}</h1>
        <p className={styles.subtle}>
          {t("PAGES.BUILDING_CONTACT.PAGE_ERROR")}
        </p>
        <Button onClick={() => void router.invalidate()}>
          {t("LOAD_ERROR.RETRY")}
        </Button>
      </Card>
    </div>
  );
}

/**
 * The retry re-runs `beforeLoad`, which retries the request behind it, so the
 * route renders nothing for a few seconds. Router `defaultPendingMs` (1s) keeps
 * this off the fast path, where the list is already cached.
 */
export function ContactLoadPending() {
  const { t } = useTranslation();

  return (
    <div
      className={`center ${styles.column}`}
      role="status"
      aria-label={t("PAGE_LOADING.LOADING")}
    >
      <LoadingSpinner size={40} />
    </div>
  );
}
